import { useNavigate } from 'react-router-dom'
import { Package } from '@phosphor-icons/react'
import { Input } from '@/ui'
import { useAuth } from '@/auth/context'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { errorMessage } from '../../api/core'
import { usePackLots, type PackLotRow } from '../../api/pack'
import { useReleaseQueue } from '../../api/quality'
import { useUrlState } from '../../lib/useUrlState'
import {
  BoardPage,
  BoardTable,
  Counter,
  Counters,
  LinkButton,
  Note,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { boardDate } from '../../model/payoutBoard'
import { kg2 } from '../../model/packing'
import { PackLotPill } from './shared'

const FILTERS: Record<string, { status?: string; hold?: string; label: string }> = {
  ALL: { label: 'All pack lots' },
  PACKING: { status: 'PACKING', label: 'Packing now' },
  CLOSED: { status: 'CLOSED', hold: 'NONE', label: 'Waiting for release' },
  RELEASED: { status: 'RELEASED', hold: 'NONE', label: 'Released' },
  HELD: { hold: 'HELD', label: 'On hold' },
}

/** Pack lots: every lot packed, its pouches and state; a row opens the pack lot's record (V1-F7-04n). */
export function PackLotsPage() {
  const { roles } = useAuth()
  // The packing/allocate route is CENTRAL_ADMIN only (routes.tsx): no button for the others.
  const canAllocate = hasAnyRole(roles, [ROLES.CENTRAL_ADMIN])
  const [f, setF] = useUrlState({ show: 'ALL', search: '', page: 1 })
  const sel = FILTERS[f.show] ?? { label: 'All pack lots' }
  const lots = usePackLots({
    status: sel.status,
    hold: sel.hold,
    search: f.search || undefined,
    page: f.page,
    limit: 50,
  })
  const waiting = useReleaseQueue({ state: 'WAITING', limit: 1 })
  const held = useReleaseQueue({ state: 'HELD', limit: 1 })
  const released = useReleaseQueue({ state: 'RELEASED', limit: 1 })
  const packing = usePackLots({ status: 'PACKING', limit: 1 })
  const navigate = useNavigate()
  const columns: BoardColumn<PackLotRow>[] = [
    {
      id: 'code',
      header: 'Pack lot',
      cell: (l) => (
        <TwoLine top={l.code} sub={l.packDate ? `packed ${boardDate(l.packDate)}` : undefined} />
      ),
    },
    {
      id: 'product',
      header: 'Product',
      cell: (l) => <TwoLine top={l.skuName} sub={`${l.skuCode} · label ${l.labelCode}`} />,
    },
    { id: 'batch', header: 'Batch', hideBelow: 'md', cell: (l) => l.batchCode ?? '—' },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (l) => l.pouchCount ?? '—' },
    {
      id: 'kg',
      header: 'Packed',
      align: 'right',
      hideBelow: 'sm',
      cell: (l) => (l.pouchCount !== null ? kg2(l.pouchCount * l.netG) : '—'),
    },
    {
      id: 'sent',
      header: 'Dispatched',
      align: 'right',
      hideBelow: 'sm',
      cell: (l) => l.pouchesDispatched || '—',
    },
    { id: 'state', header: 'State', cell: (l) => <PackLotPill status={l.status} hold={l.hold} /> },
  ]
  return (
    <BoardPage
      title="Pack lots"
      actions={
        canAllocate ? (
          <LinkButton to="/c/packing/allocate" icon={<Package aria-hidden="true" />}>
            Allocate a pack run
          </LinkButton>
        ) : undefined
      }
    >
      <Counters>
        <Counter
          label="Packing now"
          value={packing.data?.total ?? 0}
          loading={packing.isLoading}
          active={f.show === 'PACKING'}
          onClick={() => setF({ show: 'PACKING' })}
        />
        <Counter
          label="Waiting for release"
          value={waiting.data?.total ?? 0}
          loading={waiting.isLoading}
          active={f.show === 'CLOSED'}
          onClick={() => setF({ show: 'CLOSED' })}
        />
        <Counter
          label="Released"
          value={released.data?.total ?? 0}
          loading={released.isLoading}
          active={f.show === 'RELEASED'}
          onClick={() => setF({ show: 'RELEASED' })}
        />
        <Counter
          label="On hold"
          value={held.data?.total ?? 0}
          loading={held.isLoading}
          active={f.show === 'HELD'}
          onClick={() => setF({ show: 'HELD' })}
        />
      </Counters>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[16px]">{sel.label}</b>
        {f.show !== 'ALL' && (
          <button
            type="button"
            className="text-[14px] underline"
            onClick={() => setF({ show: 'ALL' })}
          >
            show all
          </button>
        )}
        <Input
          aria-label="Search a pack lot code"
          placeholder="Search a pack lot code"
          value={f.search}
          onChange={(e) => setF({ search: e.target.value })}
          className="ml-auto h-9 max-w-[240px]"
        />
      </div>
      <BoardTable<PackLotRow>
        caption="Pack lots"
        columns={columns}
        rows={lots.data?.rows ?? []}
        getRowId={(l) => l.id}
        onRowClick={(l) => navigate(`/c/packing/lots/${l.id}`)}
        loading={lots.isLoading}
        error={lots.error ? errorMessage(lots.error) : undefined}
        empty="No pack lots here."
        pager={
          lots.data
            ? {
                page: f.page,
                total: lots.data.total,
                limit: lots.data.limit,
                onPage: (page) => setF({ page }),
              }
            : undefined
        }
      />
      <Note>Nothing leaves the hall until a named quality person releases the lot (F7.6).</Note>
    </BoardPage>
  )
}
